/**
 * Reactive variable store for the global search modal.
 *
 * Uses separate reactive variables from the watchlist sort/filter stores
 * to prevent state leaking between the global search and other views
 * (follows the SE-3827 pattern with shadowTeamFilterValuesVar).
 * @module features/global-search/stores/globalSearchStore
 */

import { makeVar } from "@apollo/client";

import { IFilterValues } from "@/stores/filterValues";
import { IMenuSort, IMenuSelection } from "@/stores/selectionSort";
import { SortField } from "@/generated/graphql";

// The open / reopen-on-path navigation vars live in the shared @/stores layer
// (cross-cut by the player-detail Breadcrumb); re-exported here so this
// feature's own consumers keep a single import surface. See the module doc in
// @/stores/globalSearchNavigation for the import/no-cycle rationale.
export {
  globalSearchOpenVar,
  globalSearchReopenForPathVar,
} from "@/stores/globalSearchNavigation";

/**
 * Last-entered search query for the global search modal.
 * Persists across close/reopen within the same session (SE-5018 AC).
 * Cleared on full page reload because reactive vars are in-memory only.
 */
export const globalSearchQueryVar = makeVar<string>("");

/** Sort display setting for the global search modal */
export const globalSearchShowVar = makeVar<IMenuSelection>({
  title: "GPR",
  type: SortField.Gpr,
});

/** Sort field + order for the global search modal */
export const globalSearchSortVar = makeVar<IMenuSort | undefined>(undefined);

/**
 * Filter state for the global search modal.
 * Isolated from filterValuesVar to prevent cross-contamination (SE-3827 pattern).
 */
export const globalSearchFilterValuesVar = makeVar<IFilterValues | null>(null);

/** Whether the filter panel is shown in the global search modal */
export const globalSearchShowFilterVar = makeVar<boolean>(false);
